import { apiFetch } from 'utils/index';
import { paymentTypes } from '../paymentInputs/PaymentSelector';

/**
 * Returns true if payments are configured and enabled for cc or ach.
 *
 * @param gateway the current enabled payment gateway
 * @param gatewayConfig the cc or ach gateway config, i.e. `customer.cc_payment_info`.
 * @returns {string|*} true if payments are enabled
 */
export const paymentsEnabled = (gateway, gatewayConfig) => {
  /*
  Rules:
  * Stripe Legacy (StripeGateway) will have `stripe_user_id` set in the config if enabled.
  * SA Payments (StripeCustomGateway) will have `stripe_account_id` and `charges_enabled === true`, if enabled.
  * A migration-in-process school could have `stripe_account_id` and `charges_enabled === false`, but if the
    payment gateway is still set to `StripeGateway`, they have not yet switched to SA Payments and therefore
    only the "legacy" check applies.
  * A fully-migrated school will have gateway set to `StripeCustomGateway`. The config will still have
    `stripe_user_id` set, but we only check `stripe_account_id` and if `charges_enabled === true`.
  */
  const legacyEnabled = gateway === 'StripeGateway' && gatewayConfig.stripe_user_id;
  const saPaymentsEnabled = gateway === 'StripeCustomGateway'
    && gatewayConfig.stripe_account_id
    && gatewayConfig.charges_enabled;

  return legacyEnabled || saPaymentsEnabled;
};

/**
 * Returns true if customer gateway uses payment/setup intents
 */
export const usesIntents = (customer) => customer.cc_payment_gateway === 'StripeCustomGateway';

/**
 * Returns functions that can be used to capture payments and saved profiles.
 *
 * @param setupDetails additional options for Stripe SetupIntent
 * @param paymentDetails the payment details like billing name and address
 * @param confirmSetup the stripe function that confirms the setup intent
 * @param confirmPayment the stripe function that confirms the payment intent
 * @param elements object containing the stripe elements keyed by element type
 * @returns {{captureProfile: (function(): *), capturePayment: (function({order_transaction: *}): *)}}
 */
export const captureFunctions = ({
  setupDetails,
  paymentDetails,
  confirmSetup,
  confirmPayment,
  paymentElement,
  elements
}) => {
  const {
    name,
    email,
    address_line1: line1,
    address_line2: line2,
    address_city: city,
    address_state: state,
    address_zip: postal_code,
    address_country: country,
  } = paymentDetails;

  const trimmedEmail = email && email.trim();

  const data = {
    payment_method: {
      ...elements,
      billing_details: {
        name,
        email: trimmedEmail,
        address: {
          line1,
          line2,
          city,
          state,
          postal_code,
          country
        }
      }
    }
  };

  return {
    captureProfile: () => {
      if (paymentElement) {
        const newElements = { ...elements }  // not sure why it gets nested, but we need this in order to run stripe functions correctly

        if (global.PAYMENTS_TEST_MODE) {
          if (typeof window !== "undefined") {
            if (window.STRIPE_CUSTOM_ABANDONED) {
              return Promise.resolve({
                setupIntent: {
                  id: "seti_mock_abandoned",
                  payment_method: "pm_mock_abandoned",
                  status: "requires_payment_method",
                },
                error: {
                  message: "Please provide complete payment details.",
                  code: "incomplete_payment_details"
                }
              })
                .then(result => {
                  if (result.error) {
                    throw { message: result.error.message, code: result.error.code };
                  }
                })
            }

            if (window.STRIPE_CUSTOM_BLOCKED) {
              return Promise.resolve({
                setupIntent: {
                  id: "seti_mock_blocked",
                  payment_method: "pm_mock_blocked",
                  status: "failed",
                },
                error: {
                  message: "Payment account blocked. Please contact demo and supply error code E1000.",
                  code: "payment_method_bank_account_blocked"
                }
              })
                .then(result => {
                  if (result.error) {
                    throw { message: result.error.message, code: result.error.code };
                  }
                })
            }
          }
        }

        return (
          apiFetch.post({}, 'stripe_custom/setup_intent', setupDetails)
            .then(result => {
              if (global.PAYMENTS_TEST_MODE) {
                const id = setupDetails.payment_method == paymentTypes.CREDIT_CARD ? 'seti_mock_1' :
                  setupDetails.payment_method_types.includes('acss_debit') ? 'seti_mock_2' : 'seti_mock_3'
                return Promise.resolve({
                  setupIntent: {
                    id: id,
                    payment_method: "pm_mock_1",
                    status: "succeeded",
                  },
                });
              }

              return (
                confirmSetup({
                  elements: newElements,
                  clientSecret: result.client_secret,
                  redirect: "if_required",
                  confirmParams: {
                    payment_method_data: {
                      billing_details: {
                        name: paymentDetails.name
                      }
                    }
                  }
                })
              )
            })
            .then(result => {
              if (result.error) {
                throw { message: result.error.message, code: result.error.code };
              }

              return {
                token: result.setupIntent.id
              };
            })
        )
      }
      else {
        return apiFetch.post({}, 'stripe_custom/setup_intent', setupDetails)
          .then(result => {
            return confirmSetup(result.client_secret, data)
          })
          .then(result => {
            if (result.error) {
              throw { message: result.error.message, code: result.error.code };
            }

            return {
              token: result.setupIntent.id
            };
          })
      }
    },

    capturePayment: (order) => {
      if (paymentElement) {
        const newElements = { ...elements };

        const handleResult = result => {
          const { paymentIntent, error } = result;

          if (error) {
            throw { message: error.message, code: error.code };
          }

          /*
           * "succeeded" is obvious
           * "processing" is the status that ACH orders will usually return as
           * "requires_action" is the status when an order is using new bank account verified by micro-deposits
           */
          const success = ['succeeded', 'processing', 'requires_action'].includes(paymentIntent.status);

          if (!success) {
            throw { message: 'There was an error processing your payment. Please try again.' };
          }

          return {
            token: paymentIntent.payment_method,
            options: { payment_intent: paymentIntent }
          };
        };

        if (global.PAYMENTS_TEST_MODE) {
          const forceFailure = typeof window !== "undefined" && window.FORCE_PAYMENT_FAILURE;
          const mockResponse = forceFailure
            ? Promise.resolve({
              paymentIntent: {
                id: "pi_mock_1",
                object: "payment_intent",
                payment_method: "pm_mock_1",
                status: "failed",
                amount: "10",
              },
              error: {
                message: "Invalid request",
                code: "payment_failed"
              }
            })
            : Promise.resolve({
              paymentIntent: {
                id: "pi_mock_1",
                object: "payment_intent",
                payment_method: "pm_mock_1",
                status: "succeeded",
                amount: "10",
              }
            });

          return mockResponse.then(handleResult);
        }

        return confirmPayment({
          elements: newElements,
          clientSecret: order.transient.client_secret,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name,
                email: trimmedEmail,
                address: {
                  line1,
                  line2,
                  city,
                  state,
                  postal_code,
                  country,
                },
              },
            },
          },
          redirect: "if_required",
        }).then(handleResult);
      } else {
        return confirmPayment(order.transient.client_secret, data).then(result => {
          const { paymentIntent, error } = result;

          if (error) {
            throw { message: error.message, code: error.code };
          }

          const success = ['succeeded', 'processing', 'requires_action'].includes(paymentIntent.status);

          if (!success) {
            throw { message: 'There was an error processing your payment. Please try again.' };
          }

          return {
            token: paymentIntent.payment_method,
            options: { payment_intent: paymentIntent }
          };
        });
      }
    }
  };
};
